/**
 * The ops tiles on Background jobs — the deployed commit, the migration head, each backup leg,
 * the last site build and the deploy freeze (CSR-158; devops 07 §3, 09 §7, 10 §8, 04 §9, 03 §9).
 *
 * **"Never" is said, not left blank.** The server sends `null` for a leg with no recorded run, a
 * site never built or a revision it could not read; a blank tile reads as a pass.
 *
 * **Staleness is the alert's call, not this screen's.** How old a dump may get (alert 10) and how
 * long a drill may wait (alert 81, `backup.drill_stale_days`) are server settings. The tile shows
 * the outcome and the age as sent, and colours only a failure, so it never disagrees with the
 * alert that pages someone.
 *
 * **The freeze is a banner on every page, not only here.** `FreezeBanner` sits in the shell, so
 * whoever is about to ship sees it wherever they are; this panel keeps the calendar's row.
 *
 * No design shows this; built from docs/10-devops/07 §3 per the 2026-10-26 ruling.
 */
import { DataTable } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState } from '../components/feedback';
import { Banner, Section } from '../components/kit';
import { Button, StatusChip } from '../components/primitives';
import { errorMessage } from '../lib/api/client';
import { formatDateTime } from '../lib/datetime';
import { useOpsDrills, useOpsStatus, type OpsStatus as Status } from '../lib/api/hooks/ops';

/** A commit as people quote it: the first seven characters. */
export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}

/** "5 h ago", "3 d ago"; under an hour in minutes, because a binlog lag is measured in them. */
export function ageLabel(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${Math.round(hours)} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function migrationLine(m: Status['migrations']) {
  const heads = m.code_heads ?? [];
  const db = m.db_revision ?? 'unreadable';
  if (m.pending === true)
    return <StatusChip tone="warn" label={`Pending — database at ${db}, image ships ${heads.join(', ')}`} />;
  if (m.pending === false) return `${db} · at the shipped head`;
  return `${db} · unknown against the shipped head`;
}

function legLine(leg: Status['backups'][number]) {
  if (leg.outcome == null || leg.finished_at == null) return 'never';
  const when = `${formatDateTime(leg.finished_at)}${leg.age_hours == null ? '' : ` · ${ageLabel(leg.age_hours)}`}`;
  if (leg.outcome !== 'ok') return <StatusChip tone="error" label={`${leg.outcome} · ${when}`} />;
  return when;
}

function buildLine(build: Status['last_site_build']) {
  if (!build) return 'never';
  const what = `${build.build_id}${build.git_sha ? ` · ${shortSha(build.git_sha)}` : ''} · ${formatDateTime(build.finished_at)}`;
  if (build.exit_code !== 0) return <StatusChip tone="error" label={`exit ${build.exit_code} · ${what}`} />;
  return what;
}

function freezeLine(status: Status) {
  if (status.deploy_freeze_error) {
    return <StatusChip tone="warn" label={`Calendar malformed — ${status.deploy_freeze_error}`} />;
  }
  const freeze = status.deploy_freeze;
  if (!freeze) return 'none set';
  const span = `${formatDateTime(freeze.starts)} → ${formatDateTime(freeze.ends)} · ${freeze.reason}`;
  if (freeze.active) return <StatusChip tone="info" label={`In force · ${span}`} />;
  return `Not in force · ${span}`;
}

/** The freeze on every console page (S09): nothing until the status is read, then only when it matters. */
export function FreezeBanner() {
  const status = useOpsStatus();
  if (!status.isSuccess) return null;
  return (
    <>
      {status.data.deploy_freeze_error && (
        <Banner kind="warn" title="The freeze calendar is malformed.">
          Production deploys refuse until it is fixed — {status.data.deploy_freeze_error}
        </Banner>
      )}
      {status.data.deploy_freeze?.active && (
        <Banner kind="info" title="Deploy freeze in force.">
          {status.data.deploy_freeze.reason} · until {formatDateTime(status.data.deploy_freeze.ends)}
        </Banner>
      )}
    </>
  );
}

export function OpsStatus() {
  const status = useOpsStatus();
  return (
    <Section title="Deployment and backups" note="What production runs, and when it was last backed up and built">
      {status.isPending && <p>Reading the ops status…</p>}
      {status.isError && <p role="alert">Could not read the ops status — {errorMessage(status.error)}</p>}
      {status.isSuccess && (
        <KV
          rows={[
            ['API commit', shortSha(status.data.api_sha)],
            ['Migrations', migrationLine(status.data.migrations)],
            ...status.data.backups.map((leg) => [`Backup · ${leg.leg}`, legLine(leg)] as const),
            ['Last site build', buildLine(status.data.last_site_build)],
            ['Deploy freeze', freezeLine(status.data)],
            ['Read at', formatDateTime(status.data.read_at)],
          ]}
        />
      )}
    </Section>
  );
}

/**
 * The restore-drill register (S10, R309; devops 09 §5 and §7): each drill's outcome, finish time
 * and the dump it restored, newest first. Read-only — a drill is evidence the backups restore,
 * and the job that ran it is the only writer. A failure is coloured and its error shown as sent.
 */
export function RestoreDrills() {
  const drills = useOpsDrills();
  return (
    <Section title="Restore drills" note="Every drill as the job recorded it · newest first" pad={false}>
      {drills.isPending && <p>Reading the drills…</p>}
      {drills.isError && <p role="alert">Could not read the drills — {errorMessage(drills.error)}</p>}
      {drills.isSuccess &&
        (drills.data.pages.every((p) => (p.items ?? []).length === 0) ? (
          <EmptyState inline title="No drill recorded" body="Until one runs, nobody has shown that a backup restores." />
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'finished', label: 'Finished', mono: true },
              { key: 'outcome', label: 'Outcome' },
              { key: 'dump', label: 'Dump restored', mono: true },
              { key: 'error', label: 'Error' },
            ]}
            rows={drills.data.pages
              .flatMap((p) => p.items)
              .map((d) => ({
                id: d.id,
                finished: formatDateTime(d.finished_at),
                outcome: <StatusChip tone={d.outcome === 'ok' ? 'success' : 'error'} label={d.outcome} />,
                dump: d.object_key ?? '—',
                error: d.error_text ?? '—',
              }))}
          />
        ))}
      {drills.hasNextPage && (
        <div style={{ padding: 'var(--sp-sm) var(--sp-md)' }}>
          <Button size="sm" variant="secondary" disabled={drills.isFetchingNextPage} onClick={() => void drills.fetchNextPage()}>
            {drills.isFetchingNextPage ? 'Reading…' : 'Show older'}
          </Button>
        </div>
      )}
    </Section>
  );
}
